import { z } from 'zod';
import type { Progress } from './assessment.ts';
import type { SpeechScores } from './scoring.ts';
import { TASK_KINDS } from './tasks.ts';

// Model output: a wordy or partly empty reply (null for silence) is trimmed or defaulted instead of failing the review.
const text = (max: number) =>
  z
    .string()
    .nullish()
    .transform((s) => (s ?? '').slice(0, max));
const shortList = (max: number) =>
  z
    .array(z.string())
    .nullish()
    .transform((items) => (items ?? []).slice(0, 3).map((s) => s.slice(0, max)));

// Feedback on one spoken answer. PR C adds delivery/language scores from Stimuler's speech analysis.
export const answerFeedback = z.object({
  transcript: text(4000),
  star: z
    .object({ situation: z.boolean(), task: z.boolean(), action: z.boolean(), result: z.boolean() })
    .nullable()
    .default(null)
    .catch(null),
  strengths: shortList(160),
  improve: shortList(200),
  betterLine: z
    .string()
    .nullish()
    .transform((s) => (s ? s.slice(0, 300) : null)),
  relevance: z.enum(['on_topic', 'partly', 'off_topic', 'no_speech']).default('on_topic').catch('partly'),
  // Set by the server only (see unreviewed()); the model's reply never carries it.
  unreviewed: z.literal(true).optional(),
});
export type AnswerFeedback = z.infer<typeof answerFeedback>;

export const answerMeta = z.object({
  sessionId: z.string().min(1),
  taskId: z.string().min(1),
  kind: z.enum(TASK_KINDS),
  prompt: z.string().min(1).max(300),
  script: z.string().max(400).optional(),
});
export type AnswerMeta = z.infer<typeof answerMeta>;

// `scores` is absent when scoring is off (local runs without SCORER); `progress` only from a server before A2.
export type AnswerResponse = { feedback: AnswerFeedback; scores?: SpeechScores; progress?: Progress };

/**
 * /api/answers streams when asked (Accept: ANSWER_STREAM), one JSON object per line: the content review as soon as it
 * lands (about 4 s), then the speech scores (about 9 s), or one error when there is nothing to show. Without the
 * header it answers once with AnswerResponse, so a page loaded before a deploy keeps working.
 */
export const ANSWER_STREAM = 'application/x-ndjson';
export type AnswerEvent =
  | { feedback: AnswerFeedback }
  | { scores: SpeechScores }
  | { progress: Progress }
  | { error: string };

/**
 * Whether an answer was heard, as the server decides what counts (the assessment set, practice history) and the
 * client shows it (the warm-up steps): the review heard speech, or without a review (the stand-in counts as none) the
 * scorer scored it, since it won't score silence.
 */
export function answerHeard(
  feedback: Pick<AnswerFeedback, 'relevance' | 'unreviewed'> | null | undefined,
  scores: SpeechScores | null | undefined,
): boolean {
  return feedback && !feedback.unreviewed ? feedback.relevance !== 'no_speech' : scores?.status === 'scored';
}

/** Stands in for a review that failed when the scores came back: they were already paid for, so they are shown. */
export function unreviewed(): AnswerFeedback {
  return {
    transcript: '',
    star: null,
    strengths: [],
    improve: [],
    betterLine: null,
    relevance: 'on_topic',
    unreviewed: true,
  };
}
